/**
 * @file resolveDesktopPinPath.ts
 * @description Turns the artifact path a panel is looking at into the path a
 * pin may name. Consumers: every desktop-sync surface, which must call this
 * before reading or writing a pin — the panel's artifact path is not the pin
 * path.
 *
 * A form's attachments live on a form *version*, but you pin the *form*: the
 * agent materializes one folder per form, not one per version, so
 * `forms/{formId}/formVersions/{versionId}` folds down to `forms/{formId}`.
 * A record is pinned as it stands. `entities/{entityId}` is refused — an entity
 * is a collection of records, not an artifact with attachments, and a folder
 * for it would have nothing to hold.
 *
 * `DesktopPin`'s JSDoc records the other half of this contract: the agent
 * accepts only those two shapes and parses rather than trusts. This is the
 * client half. Anything unrecognized returns `null`; it never throws.
 */

export const resolveDesktopPinPath = (artifactPath: string | null | undefined): string | null => {
    if (!artifactPath) {
        return null;
    }

    const segments = artifactPath.split('/');
    if (segments.some((segment) => segment.length === 0)) {
        return null;
    }

    const [collection, parentId, subCollection, childId] = segments;

    if (collection === 'forms' && segments.length === 2) {
        return `forms/${parentId}`;
    }
    if (collection === 'forms' && segments.length === 4 && subCollection === 'formVersions') {
        return `forms/${parentId}`;
    }
    if (collection === 'entities' && segments.length === 4 && subCollection === 'records') {
        return `entities/${parentId}/records/${childId}`;
    }

    return null;
};
